import { useMemo } from "react"
import { Bar, BarChart, CartesianGrid, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Box, Flex, Text } from "@chakra-ui/react"
import { Chart, useChart } from "@chakra-ui/charts"
import {
  appendTimeMs,
  chartHighlightToMsRange,
  niceYAxisMax,
  timescaleChartMarginRight,
  timescaleTickFormatter,
  type ChartHighlightRange,
} from "./TimeSeries"
import { PHASE_COLORS } from "./graphColors"
import type { PhaseDataPoint } from "./PhaseBalanceChart"

const PHASES = [
  { n: 1 as const, name: "Phase 1", color: PHASE_COLORS.l1 },
  { n: 2 as const, name: "Phase 2", color: PHASE_COLORS.l2 },
  { n: 3 as const, name: "Phase 3", color: PHASE_COLORS.l3 },
]

const phaseKey = (assetId: string, n: 1 | 2 | 3) => `${assetId}__p${n}`

/**
 * How far each asset's shade is mixed toward white, by asset position. The first
 * asset keeps the standard phase colour; later ones step lighter, ordered so that
 * neighbouring assets differ most.
 */
const SHADE_STEPS = [0, 0.5, 0.25, 0.7, 0.4, 0.6]

/** `hex` (#RRGGBB) mixed toward white. The asset at `index` gets its own shade of the phase colour. */
export function shadeForAsset(hex: string, index: number): string {
  const t = SHADE_STEPS[((index % SHADE_STEPS.length) + SHADE_STEPS.length) % SHADE_STEPS.length]
  if (t === 0) return hex
  const n = Number.parseInt(hex.replace("#", ""), 16)
  const mix = (c: number) => Math.round(c + (255 - c) * t)
  const r = mix((n >> 16) & 255)
  const g = mix((n >> 8) & 255)
  const b = mix(n & 255)
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}

const REMAINDER_KEY = "_remainder"
const UNNAMED_ASSET_LABEL = "Other load"

/**
 * Assets to draw, one per asset in the phase data (phase is recorded per asset,
 * while Load series may be per asset *and connection*, e.g. `<asset>_<connection>`).
 *
 * - An asset with Load series (its id, or `<id>_...`) is drawn while any of them is
 *   ticked in the Load menu, and labelled by its series.
 * - An asset with phase data but no Load series (e.g. an inverter whose load is not
 *   in the breakdown) is the Load card's "Other": drawn while "Other" is ticked, or
 *   always when the menu has no "Other". It is labelled by its own name.
 */
export function selectPhaseAssets(
  phaseData: PhaseDataPoint[],
  selectedConsumers: Set<string>,
  consumerLabelMap?: Map<string, string>
): Array<{ id: string; label: string }> {
  const seriesNames = consumerLabelMap
    ? Array.from(consumerLabelMap.keys()).filter((k) => k !== REMAINDER_KEY)
    : []
  const hasRemainder = consumerLabelMap?.has(REMAINDER_KEY) ?? false
  const out: Array<{ id: string; label: string }> = []
  const seen = new Set<string>()
  for (const point of phaseData) {
    const id = point.consumerId
    if (seen.has(id)) continue
    seen.add(id)
    const series = seriesNames.filter((n) => n === id || n.startsWith(`${id}_`))
    if (series.length > 0) {
      if (!series.some((n) => selectedConsumers.has(n))) continue
      const label =
        series.length === 1
          ? (consumerLabelMap?.get(series[0]) ?? id)
          : (point.assetName ?? consumerLabelMap?.get(series[0]) ?? id)
      out.push({ id, label })
    } else {
      if (hasRemainder && !selectedConsumers.has(REMAINDER_KEY)) continue
      out.push({ id, label: point.assetName ?? UNNAMED_ASSET_LABEL })
    }
  }
  return out
}

export interface PerAssetPhaseChartProps {
  phaseData: PhaseDataPoint[]
  /** Consumers currently ticked in the Load menu. */
  selectedConsumers: Set<string>
  timeDomain: [number, number]
  xAxisTicksMs?: number[]
  scaleToKw: boolean
  sharedTimestamps?: string[]
  syncId?: string
  /** Load series name -> label (includes `_remainder` when the Load menu has "Other"). */
  consumerLabelMap?: Map<string, string>
  chartHeight?: string
  highlightRanges?: ChartHighlightRange[]
  highlightPhase?: 1 | 2 | 3
}

/**
 * Phase balance as a stacked bar per time bucket (phase 1, 2, 3), where each phase
 * is split into a segment per ticked asset, in that asset's shade of the phase
 * colour. The tooltip lists each phase's total and its per-asset values.
 */
const PerAssetPhaseChart = ({
  phaseData,
  selectedConsumers,
  timeDomain,
  xAxisTicksMs,
  scaleToKw,
  sharedTimestamps,
  syncId,
  consumerLabelMap,
  chartHeight = "400px",
  highlightRanges,
  highlightPhase,
}: PerAssetPhaseChartProps) => {
  const unit = scaleToKw ? "kW" : "W"

  const assets = useMemo(
    () => selectPhaseAssets(phaseData, selectedConsumers, consumerLabelMap),
    [phaseData, selectedConsumers, consumerLabelMap]
  )

  const assetIds = useMemo(() => new Set(assets.map((a) => a.id)), [assets])

  // Shade by position among all assets with phase data, so an asset keeps its shade
  // when others are unticked in the Load menu.
  const shadeIndex = useMemo(() => {
    const order = new Map<string, number>()
    for (const p of phaseData) if (!order.has(p.consumerId)) order.set(p.consumerId, order.size)
    return order
  }, [phaseData])
  const colorFor = (assetId: string, ph: (typeof PHASES)[number]) =>
    shadeForAsset(ph.color, shadeIndex.get(assetId) ?? 0)

  const rows = useMemo(() => {
    const scale = scaleToKw ? 1 / 1000 : 1
    const byTs = new Map<string, Record<string, number | string>>()
    for (const p of phaseData) {
      if (!assetIds.has(p.consumerId)) continue
      const row = byTs.get(p.timestamp) ?? { timestamp: p.timestamp }
      row[phaseKey(p.consumerId, 1)] = Math.max(0, p.phase1 ?? 0) * scale
      row[phaseKey(p.consumerId, 2)] = Math.max(0, p.phase2 ?? 0) * scale
      row[phaseKey(p.consumerId, 3)] = Math.max(0, p.phase3 ?? 0) * scale
      byTs.set(p.timestamp, row)
    }
    const timestamps = sharedTimestamps ?? Array.from(byTs.keys()).sort()
    return appendTimeMs(timestamps.map((ts) => byTs.get(ts) ?? { timestamp: ts }) as never[])
  }, [phaseData, assetIds, sharedTimestamps, scaleToKw])

  const series = useMemo(
    () =>
      assets.flatMap((a) =>
        PHASES.map((ph) => ({ name: phaseKey(a.id, ph.n), color: colorFor(a.id, ph), label: ph.name, strokeWidth: 0 }))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assets, shadeIndex]
  )
  const chart = useChart({ data: rows, series } as unknown as Parameters<typeof useChart>[0])
  const formatTick = useMemo(() => timescaleTickFormatter(Math.abs(timeDomain[1] - timeDomain[0])), [timeDomain])

  const fmt = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })

  const TooltipContent = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: number }) => {
    if (!active || !payload?.length) return null
    const row = payload[0].payload as Record<string, number | string>
    const when = new Date(Number(label)).toLocaleString([], {
      weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
    })
    return (
      <Box backgroundColor="card" borderWidth="1px" borderColor="border" borderRadius="md" padding={3} boxShadow="lg" minW="220px">
        <Text fontSize="sm" fontWeight="bold" color="primary" mb={2}>{when}</Text>
        {PHASES.slice().reverse().map((ph) => {
          const rowsForPhase = assets.map((a) => {
            const v = row[phaseKey(a.id, ph.n)]
            return { asset: a, value: typeof v === "number" ? v : 0 }
          })
          const total = rowsForPhase.reduce((sum, r) => sum + r.value, 0)
          return (
            <Box key={ph.n} mb={1}>
              <Flex alignItems="center" gap={2}>
                <Box w="10px" h="10px" borderRadius="sm" bg={ph.color} flexShrink={0} />
                <Text fontSize="sm" color="primary" flex="1">{ph.name}</Text>
                <Text fontSize="sm" fontWeight="semibold" color="primary">{fmt(total)} {unit}</Text>
              </Flex>
              {assets.length > 1 &&
                rowsForPhase.map(({ asset, value }) => (
                  <Flex key={asset.id} alignItems="center" gap={2} ml="18px">
                    <Box w="8px" h="8px" borderRadius="sm" bg={colorFor(asset.id, ph)} flexShrink={0} />
                    <Text fontSize="xs" color="fg.muted" flex="1" truncate>{asset.label}</Text>
                    <Text fontSize="xs" fontWeight="medium" color="primary">{fmt(value)} {unit}</Text>
                  </Flex>
                ))}
            </Box>
          )
        })}
      </Box>
    )
  }

  if (assets.length === 0) return null

  return (
    <Box position="relative" css={{ "& .recharts-wrapper": { overflow: "visible" }, "& .recharts-tooltip-wrapper": { overflow: "visible" } }}>
      <Flex alignItems="center" justifyContent="center" gap={4} mb={1} flexWrap="wrap">
        {PHASES.slice().reverse().map((ph) => (
          <Flex key={ph.n} alignItems="center" gap={1} fontSize="sm">
            <Box w="12px" h="12px" bg={ph.color} borderRadius="sm" />
            <Text color="primary">{ph.name}</Text>
          </Flex>
        ))}
      </Flex>
      {assets.length > 1 && (
        <Flex alignItems="center" justifyContent="center" gap={4} mb={1} flexWrap="wrap">
          {assets.map((a) => (
            <Flex key={a.id} alignItems="center" gap={1} fontSize="xs">
              {PHASES.map((ph) => (
                <Box key={ph.n} w="8px" h="12px" bg={colorFor(a.id, ph)} borderRadius="sm" />
              ))}
              <Text color="primary" ml={1}>{a.label}</Text>
            </Flex>
          ))}
        </Flex>
      )}
      <Chart.Root h={chartHeight} maxH={chartHeight} chart={chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            syncId={syncId}
            margin={{ top: 15, right: timescaleChartMarginRight(false), left: 20, bottom: 60 }}
            barCategoryGap="6%"
          >
            <CartesianGrid stroke={chart.color("border")} strokeDasharray="3 3" vertical={false} />
            {highlightRanges?.map((range, index) => {
              const { x1, x2 } = chartHighlightToMsRange(range)
              return (
                <ReferenceArea
                  key={`highlight-${index}`}
                  x1={x1}
                  x2={x2}
                  fill={range.color || "rgba(255, 165, 0, 0.2)"}
                  fillOpacity={0.3}
                  stroke={range.color || "rgba(255, 165, 0, 0.6)"}
                  strokeWidth={4}
                />
              )
            })}
            <XAxis
              dataKey="timeMs"
              type="number"
              scale="time"
              domain={timeDomain}
              axisLine={false}
              stroke={chart.color("chart_axis")}
              ticks={xAxisTicksMs}
              tickLine={false}
              fontSize={12}
              tickFormatter={formatTick}
              minTickGap={40}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              stroke={chart.color("chart_axis")}
              fontSize={12}
              domain={[0, (dataMax: number) => niceYAxisMax(dataMax)]}
              label={{ value: `Phase load (${unit})`, angle: -90, position: "insideLeft" }}
            />
            <Tooltip
              animationDuration={0}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ zIndex: 1000, pointerEvents: "none", overflow: "visible" }}
              cursor={{ fill: "rgba(0, 0, 0, 0.06)" }}
              content={<TooltipContent />}
            />
            {/* One stack per interval, as the combined chart was: phase 1 at the bottom,
                phase 3 on top. Each phase is split into one segment per asset, in that
                asset's shade of the phase colour. */}
            {PHASES.flatMap((ph) =>
              assets.map((a) => (
                <Bar
                  key={phaseKey(a.id, ph.n)}
                  dataKey={phaseKey(a.id, ph.n)}
                  stackId="phase"
                  fill={colorFor(a.id, ph)}
                  fillOpacity={highlightPhase && highlightPhase !== ph.n ? 0.2 : 1}
                  isAnimationActive={false}
                  maxBarSize={80}
                />
              ))
            )}
          </BarChart>
        </ResponsiveContainer>
      </Chart.Root>
    </Box>
  )
}

export default PerAssetPhaseChart
